import React, {useCallback, useEffect, useMemo, useState, useContext} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import DayPickerInputRange from './DayPickerInputRange';
import UserStore from '../stores/UserStore';
import config from '../config.js';
import {I18nContext} from 'utils/I18n/context';

const keyCustom = 'custom';

const DatePickerRanges = ({from: pFrom, to: pTo, onAction, onCancel, className}) => {
    const {t} = useContext(I18nContext) || {};
    const timezone = UserStore.getTimeZone();
    const rangeNames = t?.dataTable?.rangeNames || {};
    const dataPicker = t?.dataTable?.dataPicker || {customRangeLabel: 'Your period'};
    const staticActions = t?.actions || {apply: 'Apply', cancel: 'Cancel'};

    const fromDay = useMemo(() => moment().startOf('day').tz(timezone), [timezone]);
    const toDay = useMemo(() => moment().endOf('day').tz(timezone), [timezone]);

    const [from, setFrom] = useState(pFrom || moment().tz(timezone));
    const [to, setTo] = useState(pTo || moment().tz(timezone));
    const [fromInputRange, setFromInputRange] = useState(undefined);
    const [toInputRange, setToInputRange] = useState(undefined);
    const [isActiveCalendar, setIsActiveCalendar] = useState(false);
    const [disabledApply] = useState(false);
    const [activeKeyRange, setActiveKeyRange] = useState('');
    const ranges = useMemo(() => ({
        [rangeNames.today || 'Today']: [fromDay, toDay],
        [rangeNames.yesterday || 'Yesterday']: [fromDay.clone().subtract(1, 'days'), toDay.clone().subtract(1, 'days')],
        [rangeNames.week || 'Week']: [fromDay.clone().subtract(6, 'days'), toDay],
        [rangeNames.month || 'Month']: [fromDay.clone().subtract(1, 'months'), toDay],
        [rangeNames.year || 'Year']: [fromDay.clone().subtract(1, 'year'), toDay],
    }), [rangeNames, fromDay, toDay]);

    const getActiveKeyRange = useCallback((f = from, t0 = to) => {
        let key = keyCustom;
        Object.keys(ranges).forEach((rk) => {
            const r = ranges[rk];
            if (f.format(config.codeFormat) === r[0].format(config.codeFormat) && t0.format(config.codeFormat) === r[1].format(config.codeFormat)) {
                key = rk;
            }
        });
        return key;
    }, [ranges, from, to]);

    useEffect(() => {
        const initKey = getActiveKeyRange();
        setActiveKeyRange((prev) => prev || initKey);
        setIsActiveCalendar((prev) => (prev === true ? true : initKey === keyCustom));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const f = pFrom || moment().tz(timezone);
        const t0 = pTo || moment().tz(timezone);
        const changed =
            from.format(config.codeFormat) !== f.format(config.codeFormat) ||
            to.format(config.codeFormat) !== t0.format(config.codeFormat);
        if (changed) {
            setFrom(f);
            setTo(t0);
            setFromInputRange(undefined);
            setToInputRange(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pFrom, pTo, timezone]);

    const onOpenCalendar = useCallback(() => {
        setActiveKeyRange(keyCustom);
        setIsActiveCalendar(true);
    }, [setActiveKeyRange]);
    const onSetRange = useCallback((e) => {
        const key = e.target.getAttribute('data-key');
        const range = ranges[key];
        if (!range) return;
        const f = range[0];
        const t0 = range[1];
        setFrom(f);
        setTo(t0);
        setFromInputRange(undefined);
        setToInputRange(undefined);
        setActiveKeyRange(getActiveKeyRange(f, t0));
        setIsActiveCalendar(false);
        onAction && onAction(f, t0);
    }, [ranges, getActiveKeyRange, onAction]);

    const onUpdateInputRange = useCallback((f, t0) => {
        setFromInputRange(f);
        setToInputRange(t0);
    }, []);
    const onSetInputRange = useCallback(() => {
        const f = fromInputRange || from;
        const t0 = toInputRange || to;
        setFrom(f);
        setTo(t0);
        setActiveKeyRange(keyCustom);
        setIsActiveCalendar(true);
        onAction && onAction(f, t0);
    }, [fromInputRange, toInputRange, from, to, onAction]);

    const onCancelLocal = useCallback(() => {
        onCancel && onCancel();
    }, [onCancel]);

    const getClassListItem = useCallback((key) => classNames({
        'ranges-list__item': true,
        active: activeKeyRange === key
    }), [activeKeyRange]);

    return (
        <div className={`ranges ${className ? className : ''}`}>
            <div className="ranges-left">
                <ul className="ranges-list">
                    {Object.keys(ranges).map((key, i) => (
                        <li className={getClassListItem(key)} key={`rg-${i}`} onClick={onSetRange}
                            data-key={key}>{key}</li>))}
                    <li className={getClassListItem(keyCustom)}
                        onClick={onOpenCalendar}>{dataPicker.customRangeLabel}</li>
                </ul>
                <div className="ranges-toolbar">
                    <button className="btn btn-sm btn-success" type="button" disabled={disabledApply}
                            onClick={onSetInputRange}>{staticActions.apply}</button>
                    <button className="btn btn-sm btn-default" type="button"
                            onClick={onCancelLocal}>{staticActions.cancel}</button>
                </div>
            </div>
            <div className={`ranges-calendar${isActiveCalendar ? ' active' : ''}`}>
                <DayPickerInputRange from={fromInputRange || from} to={toInputRange || to}
                                     onDayChange={onUpdateInputRange}/>
            </div>
        </div>
    );
};

DatePickerRanges.propTypes = {
    from: PropTypes.any,
    to: PropTypes.any,
    onAction: PropTypes.func,
    onCancel: PropTypes.func,
    className: PropTypes.string
};
DatePickerRanges.defaultProps = {};


export default DatePickerRanges;
